// Staff copy for the Floor and Appointments in the new design (pages/Floor.tsx,
// pages/Appointments.tsx, ui/hallParts.tsx): short labels, the Floor's one
// sentence about the hall, and the toasts. Kept apart from floorBoard and
// appointments, whose longer sentences the older screens were built around.
// Native review pending (az, ru): the Azerbaijani and Russian here are a first
// draft. Change a phrase in all three languages at once, never in one.
import type { Phrase } from '../staffText'

export const hall = {
  // Durations, calm rather than a ticking clock.
  'hall.min': { en: '{n} min', az: '{n} dəq', ru: '{n} мин' },
  'hall.hmin': { en: '{h} h {m} min', az: '{h} saat {m} dəq', ru: '{h} ч {m} мин' },
  'hall.now': { en: 'now', az: 'indi', ru: 'сейчас' },

  // The Floor's one sentence. {queue}, {desk} and {time} are drawn in bold.
  'hall.say.over': { en: '{queue} is {time} over target.', az: '{queue} hədəfi {time} keçib.', ru: '{queue}: норма превышена на {time}.' },
  'hall.say.free': { en: '{desk} is free.', az: '{desk} boşdur.', ru: '{desk} свободно.' },
  'hall.say.break': { en: '{desk} is on a break.', az: '{desk} fasilədədir.', ru: '{desk} на перерыве.' },
  'hall.say.busy': { en: 'Every counter that calls it is busy.', az: 'Onu çağıran bütün pəncərələr məşğuldur.', ru: 'Все окна, которые её вызывают, заняты.' },
  'hall.say.noDesk': { en: 'No open counter calls it.', az: 'Onu heç bir açıq pəncərə çağırmır.', ru: 'Её не вызывает ни одно открытое окно.' },
  'hall.say.orphan': { en: '{queue} has {n} waiting and no open counter calls it.', az: '{queue}: {n} nəfər gözləyir, onu heç bir açıq pəncərə çağırmır.', ru: '{queue}: ждут {n}, и её не вызывает ни одно открытое окно.' },
  'hall.say.onTarget': { en: 'All lines on target.', az: 'Bütün növbələr hədəf daxilindədir.', ru: 'Все очереди в пределах нормы.' },
  'hall.say.empty': { en: 'Nobody waiting.', az: 'Gözləyən yoxdur.', ru: 'Никто не ждёт.' },
  'hall.lateOne': { en: '{who}, {time}, is late', az: '{who}, {time}, gecikir', ru: '{who}, {time}, опаздывает' },
  'hall.lateMany': { en: '{n} bookings late', az: '{n} bron gecikir', ru: 'опаздывают по записи: {n}' },

  // The three numbers.
  'hall.waitingNow': { en: 'Waiting now', az: 'İndi gözləyir', ru: 'Ждут сейчас' },
  'hall.desksOpen': { en: 'Counters open', az: 'Açıq pəncərələr', ru: 'Открыто окон' },
  'hall.ofN': { en: 'of {n}', az: '/ {n}', ru: 'из {n}' },
  'hall.servedToday': { en: 'Served today', az: 'Bu gün xidmət göstərilib', ru: 'Обслужено сегодня' },

  // Lines.
  'hall.lines': { en: 'Lines', az: 'Növbələr', ru: 'Очереди' },
  'hall.longest': { en: '{time} longest', az: 'ən uzun {time}', ru: 'дольше всех {time}' },
  'hall.target': { en: 'target {time}', az: 'hədəf {time}', ru: 'норма {time}' },
  'hall.nobodyWaiting': { en: 'Nobody waiting', az: 'Gözləyən yoxdur', ru: 'Никто не ждёт' },
  'hall.more': { en: '{n} more', az: 'daha {n}', ru: 'ещё {n}' },
  'hall.fewer': { en: 'Show fewer', az: 'Azını göstər', ru: 'Свернуть' },
  'hall.calledBy': { en: 'Called by {desks}', az: 'Çağırır: {desks}', ru: 'Вызывает: {desks}' },
  'hall.lineWaiting': { en: '{queue}, {n} waiting', az: '{queue}, {n} gözləyir', ru: '{queue}, ждут: {n}' },
  'hall.tag.priority': { en: 'Priority', az: 'Prioritet', ru: 'Приоритет' },
  'hall.tag.booked': { en: 'Booked', az: 'Bron', ru: 'Запись' },

  // Desks.
  'hall.tile.break': { en: 'On a break', az: 'Fasilədə', ru: 'Перерыв' },
  'hall.tile.free': { en: 'Free', az: 'Boş', ru: 'Свободно' },
  'hall.tile.calling': { en: 'Calling {number}', az: '{number} çağırılır', ru: 'Вызов {number}' },
  'hall.tile.serving': { en: 'Serving {number}', az: '{number} xidmətdə', ru: 'Обслуживает {number}' },
  'hall.tile.over': { en: '{time}, over limit', az: '{time}, hədd keçib', ru: '{time}, сверх лимита' },
  'hall.tile.wrapup': { en: 'Wrapping up {number}', az: '{number} tamamlanır', ru: 'Завершает {number}' },
  'hall.tile.waiting': { en: '{n} waiting', az: '{n} gözləyir', ru: 'ждут: {n}' },
  'hall.nobodyAt': { en: 'Nobody at {desks}', az: 'Boş: {desks}', ru: 'Никого: {desks}' },
  'hall.clerk': { en: 'Clerk', az: 'Əməkdaş', ru: 'Сотрудник' },
  'hall.signedIn': { en: 'Signed in', az: 'Daxil olub', ru: 'В системе' },
  'hall.since': { en: '{time}, {ago}', az: '{time}, {ago}', ru: '{time}, {ago}' },
  'hall.profile': { en: 'Work profile', az: 'İş profili', ru: 'Рабочий профиль' },
  'hall.calls': { en: 'Calls', az: 'Çağırır', ru: 'Вызывает' },
  'hall.rule': { en: 'Rule', az: 'Qayda', ru: 'Правило' },
  'hall.heldHere': { en: 'Held for it', az: 'Onun üçün saxlanılır', ru: 'Отложено для него' },
  'hall.none': { en: 'None', az: 'Yoxdur', ru: 'Нет' },
  'hall.standingBy': { en: 'Calls the next one itself', az: 'Növbətini özü çağırır', ru: 'Вызывает следующего сам' },
  'hall.doing.break': { en: 'On a break, {time}', az: 'Fasilədə, {time}', ru: 'Перерыв, {time}' },
  'hall.doing.paused': { en: '{what}, {time}', az: '{what}, {time}', ru: '{what}, {time}' },
  'hall.doing.free': { en: 'Free, {n} waiting', az: 'Boş, {n} gözləyir', ru: 'Свободно, ждут: {n}' },
  'hall.doing.freeNobody': { en: 'Free, nobody waiting', az: 'Boş, gözləyən yoxdur', ru: 'Свободно, никто не ждёт' },
  'hall.doing.calling': { en: 'Calling {number}, {time}', az: '{number} çağırılır, {time}', ru: 'Вызов {number}, {time}' },
  'hall.doing.serving': { en: 'Serving {number}, {time}', az: '{number} xidmətdə, {time}', ru: 'Обслуживает {number}, {time}' },
  'hall.doing.wrapup': { en: 'Wrapping up {number}', az: '{number} tamamlanır', ru: 'Завершает {number}' },

  // Set aside and missed.
  'hall.aside': { en: 'Set aside and missed', az: 'Kənara qoyulanlar və gəlməyənlər', ru: 'Отложенные и не явившиеся' },
  'hall.nobody': { en: 'Nobody', az: 'Heç kim', ru: 'Никого' },
  'hall.setAsideAt': { en: 'Set aside at {desk}', az: '{desk} kənara qoyub', ru: 'Отложен: {desk}' },
  'hall.setAside': { en: 'Set aside', az: 'Kənara qoyulub', ru: 'Отложен' },
  'hall.returned': { en: 'Back', az: 'Qayıdıb', ru: 'Вернулся' },
  'hall.missedAt': { en: 'Missed at {desk}', az: '{desk} çağırışına gəlmədi', ru: 'Не явился: {desk}' },
  'hall.missed': { en: 'Missed', az: 'Gəlmədi', ru: 'Не явился' },
  'hall.forDesk': { en: 'for {desk}', az: '{desk} üçün', ru: 'ждёт: {desk}' },
  'hall.forAnyDesk': { en: 'for any counter', az: 'istənilən pəncərə üçün', ru: 'ждёт: любое окно' },
  'hall.forPerson': { en: 'for {name}', az: '{name} üçün', ru: 'ждёт: {name}' },
  'hall.forSomeone': { en: 'for one person', az: 'bir nəfər üçün', ru: 'ждёт: сотрудник' },

  // A waiting ticket's panel.
  'hall.ticket': { en: 'Ticket {number}', az: 'Talon {number}', ru: 'Талон {number}' },
  'hall.cameFor': { en: 'Came for', az: 'Xidmət', ru: 'Услуга' },
  'hall.waiting': { en: 'Waiting', az: 'Gözləyir', ru: 'Ждёт' },
  'hall.waitedOf': { en: '{time}, target {target}', az: '{time}, hədəf {target}', ru: '{time}, норма {target}' },
  'hall.placeInLine': { en: 'Place in line', az: 'Növbədə yeri', ru: 'Место в очереди' },
  'hall.nOfM': { en: '{n} of {m}', az: '{n} / {m}', ru: '{n} из {m}' },
  'hall.calledByLabel': { en: 'Called by', az: 'Çağıran', ru: 'Вызывает' },
  'hall.noOpenDesk': { en: 'No open counter', az: 'Açıq pəncərə yoxdur', ru: 'Нет открытых окон' },
  'hall.note': { en: 'Note', az: 'Qeyd', ru: 'Заметка' },
  'hall.move': { en: 'Move', az: 'Köçür', ru: 'Переместить' },
  'hall.voidTicket': { en: 'Void ticket', az: 'Talonu ləğv et', ru: 'Аннулировать талон' },

  // Desk requests: the Floor's Requests list, and the banner that links to it
  // from every other staff screen (Counter, Reception).
  'hall.requests.title': { en: 'Requests', az: 'Sorğular', ru: 'Запросы' },
  'hall.requests': { en: '{n, plural, one {# request} other {# requests}}', az: '{n} sorğu', ru: 'Запросов: {n}' },
  'hall.requests.waitingFor': { en: 'waiting {time}', az: '{time} gözləyir', ru: 'ждёт {time}' },
  'hall.requests.approve': { en: 'Approve', az: 'Təsdiqlə', ru: 'Одобрить' },
  'hall.requests.declineAsk': { en: 'Decline…', az: 'Rədd et…', ru: 'Отказать…' },
  'hall.requests.decline': { en: 'Decline', az: 'Rədd et', ru: 'Отказать' },
  'hall.requests.reasonPlaceholder': { en: 'Reason (optional)', az: 'Səbəb (istəyə görə)', ru: 'Причина (необязательно)' },

  // Toasts.
  'hall.done.signedOut': { en: '{desk} signed out', az: '{desk} bağlandı', ru: '{desk}: сеанс завершён' },
  'hall.done.back': { en: '{number} back in line', az: '{number} növbəyə qayıtdı', ru: '{number} снова в очереди' },
  'hall.done.void': { en: '{number} voided', az: '{number} ləğv edildi', ru: '{number} аннулирован' },
  'hall.done.putBack': { en: '{number} put back', az: '{number} qaytarıldı', ru: '{number} возвращён' },
  'hall.done.moved': { en: '{number} moved', az: '{number} köçürüldü', ru: '{number} перемещён' },
  'hall.done.arrived': { en: '{who} is {number}', az: '{who}: {number}', ru: '{who}: {number}' },
  'hall.done.cancelled': { en: '{reference} cancelled', az: '{reference} ləğv edildi', ru: 'Запись {reference} отменена' },
  'hall.done.booked': { en: 'Booked, code {code}', az: 'Bron edildi, kod {code}', ru: 'Записано, код {code}' },
  'hall.done.saved': { en: 'Saved', az: 'Yadda saxlandı', ru: 'Сохранено' },
  'hall.done.approved': { en: 'Approved', az: 'Təsdiqləndi', ru: 'Одобрено' },
  'hall.done.declined': { en: 'Declined', az: 'Rədd edildi', ru: 'Отказано' },

  // Appointments.
  'hall.today': { en: 'Today', az: 'Bu gün', ru: 'Сегодня' },
  'hall.tomorrow': { en: 'Tomorrow', az: 'Sabah', ru: 'Завтра' },
  'hall.tally.arrived': { en: '{n} arrived', az: '{n} gəlib', ru: 'пришли: {n}' },
  'hall.tally.toCome': { en: '{n} still to come', az: '{n} gözlənilir', ru: 'ожидаются: {n}' },
  'hall.tally.neverCame': { en: '{n} never came', az: '{n} gəlmədi', ru: 'не пришли: {n}' },
  'hall.tally.late': { en: '{n} late', az: '{n} gecikir', ru: 'опаздывают: {n}' },
  'hall.tally.missed': { en: '{n} did not come', az: '{n} gəlmədi', ru: 'не пришли: {n}' },
  'hall.tally.cancelled': { en: '{n} cancelled', az: '{n} ləğv edilib', ru: 'отменено: {n}' },
  'hall.dayBefore': { en: 'Day before', az: 'Əvvəlki gün', ru: 'Предыдущий день' },
  'hall.dayAfter': { en: 'Day after', az: 'Növbəti gün', ru: 'Следующий день' },
  'hall.showOn': { en: 'Show bookings on', az: 'Bronları göstər:', ru: 'Записи на' },
  'hall.import': { en: 'Import', az: 'Yüklə', ru: 'Загрузить' },
  'hall.add': { en: 'Add booking', az: 'Bron əlavə et', ru: 'Добавить запись' },
  'hall.settings': { en: 'Settings', az: 'Ayarlar', ru: 'Настройки' },
  'hall.nobodyBooked': { en: 'Nobody booked', az: 'Bron yoxdur', ru: 'Записей нет' },
  'hall.noMatch': { en: 'No booking matches “{q}”', az: '«{q}» üzrə bron yoxdur', ru: 'Нет записи по запросу «{q}»' },
  'hall.showEarlier': { en: 'Show {n} earlier', az: 'Əvvəlki {n} bronu göstər', ru: 'Показать ранние: {n}' },
  'hall.hideEarlier': { en: 'Hide earlier', az: 'Əvvəlkiləri gizlət', ru: 'Скрыть ранние' },
  'hall.nowAt': { en: 'Now {time}', az: 'İndi {time}', ru: 'Сейчас {time}' },
  'hall.refCode': { en: '{reference}, code {code}', az: '{reference}, kod {code}', ru: '{reference}, код {code}' },
  'hall.st.late': { en: '{time} late', az: '{time} gecikir', ru: 'опаздывает на {time}' },
  'hall.st.due': { en: 'Due in {time}', az: '{time} sonra', ru: 'Через {time}' },
  'hall.st.waiting': { en: 'Waiting as {number}', az: '{number} kimi gözləyir', ru: 'Ждёт: {number}' },
  'hall.giveTicket': { en: 'Give a ticket', az: 'Talon ver', ru: 'Выдать талон' },
  'hall.service': { en: 'Service', az: 'Xidmət', ru: 'Услуга' },
  'hall.reference': { en: 'Reference', az: 'Bron nömrəsi', ru: 'Номер записи' },
  'hall.code': { en: 'Code', az: 'Kod', ru: 'Код' },
  'hall.manageLink': { en: 'Link', az: 'Keçid', ru: 'Ссылка' },
  'hall.copyLink': { en: 'Copy', az: 'Kopyala', ru: 'Копировать' },
  'hall.showLink': { en: 'Show link', az: 'Keçidi göstər', ru: 'Показать ссылку' },
  'hall.codeSame': { en: 'Same as reference', az: 'Bron nömrəsi ilə eyni', ru: 'Как номер записи' },
  'hall.status': { en: 'Status', az: 'Vəziyyət', ru: 'Статус' },
  'hall.day': { en: 'Day', az: 'Gün', ru: 'День' },
  'hall.time': { en: 'Time', az: 'Vaxt', ru: 'Время' },
  'hall.book': { en: 'Book {time}', az: '{time} üçün bron et', ru: 'Записать на {time}' },
  'hall.bookPlain': { en: 'Book', az: 'Bron et', ru: 'Записать' },
  'hall.importTitle': { en: 'Import bookings', az: 'Bronları yüklə', ru: 'Загрузка записей' },
  'hall.importHint': { en: 'CSV, one booking a line. First line:', az: 'CSV, hər sətirdə bir bron. Birinci sətir:', ru: 'CSV, одна запись в строке. Первая строка:' },
  'hall.file': { en: 'File', az: 'Fayl', ru: 'Файл' },
  'hall.new': { en: 'New', az: 'Yeni', ru: 'Новые' },
  'hall.changed': { en: 'Changed', az: 'Dəyişib', ru: 'Изменённые' },
  'hall.unchanged': { en: 'Unchanged', az: 'Dəyişməyib', ru: 'Без изменений' },
  'hall.refused': { en: 'Refused', az: 'Rədd edilib', ru: 'Отклонено' },
  'hall.line': { en: 'Line', az: 'Sətir', ru: 'Строка' },
  'hall.reason': { en: 'Reason', az: 'Səbəb', ru: 'Причина' },
  'hall.importN': { en: 'Import {n}', az: 'Yüklə: {n}', ru: 'Загрузить: {n}' },
} satisfies Record<`hall.${string}`, Phrase>
